import React, { useEffect, useMemo, useState } from "react";
import Latex from "react-latex-next";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { ImCross } from "react-icons/im";
import { Button } from "react-bootstrap";
import { ZoomImage } from "../ZoomImage/ZoomImage";
import { Question } from "../../types";
import { BsArrowCounterclockwise, BsXCircle } from "react-icons/bs";
import { shouldIgnoreQuestionNavigationShortcut } from "../../utils/shouldIgnoreQuestionNavigationShortcut";

type McqPracticeBlockProps = {
  question: Question;
  practiceQuestionAnswered: boolean;
  selectedAnswerId?: number;
  onChangeSelectedAnswer: (answerOptionId?: number) => void;
  delimeters: any[];
  onSubmit: () => void;
};

export const McqPracticeBlock: React.FC<McqPracticeBlockProps> = ({
  question,
  practiceQuestionAnswered,
  selectedAnswerId,
  onChangeSelectedAnswer,
  delimeters,
  onSubmit,
}) => {
  const ABCDE = "ABCDE";
  const [eliminatedIds, setEliminatedIds] = useState<Set<number>>(new Set());

  const toggleEliminated = (answerOptionId: number) => {
    if (practiceQuestionAnswered) return;
    setEliminatedIds((prev) => {
      const next = new Set(prev);
      if (next.has(answerOptionId)) {
        next.delete(answerOptionId);
      } else {
        next.add(answerOptionId);
      }
      return next;
    });
    if (selectedAnswerId === answerOptionId) {
      onChangeSelectedAnswer(undefined);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (practiceQuestionAnswered) return;
      if (shouldIgnoreQuestionNavigationShortcut(e)) {
        return;
      }
      const upper = e.key?.toUpperCase?.();
      const index = ABCDE.indexOf(upper);
      if (index >= 0 && index < (question.answerOptions?.length || 0)) {
        const ao = question.answerOptions[index];
        if (!eliminatedIds.has(ao.id)) {
          onChangeSelectedAnswer(ao.id);
        }
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter') {
        onSubmit();
        e.preventDefault();
        return;
      }
      if (e.key === 'Escape') {
        setEliminatedIds(new Set());
        onChangeSelectedAnswer(undefined);
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [practiceQuestionAnswered, question.answerOptions, eliminatedIds, onChangeSelectedAnswer, onSubmit]);

  const handleCardClick = (e: any, answerOptionId: number) => {
    const userClickedOnSurroundingDivToZoomOutOfImage = e.target?.hasAttribute?.(
      "data-rmiz-modal-content",
    );
    if (
      practiceQuestionAnswered ||
      e.target?.tagName === "IMG" ||
      userClickedOnSurroundingDivToZoomOutOfImage
    ) {
      return;
    }

    if (eliminatedIds.has(answerOptionId)) {
      return;
    }

    if (selectedAnswerId === answerOptionId) {
      onChangeSelectedAnswer(undefined);
    } else {
      onChangeSelectedAnswer(answerOptionId);
    }
  };

  return (
    <>
      <div className="answer-options-wrapper">
      {question?.answerOptions.map((answerOption, index) => {
        const option = ABCDE[index];
          const classes = ["answer-option"] as string[];
        if (practiceQuestionAnswered) {
          classes.push("locked");
          if (answerOption.isCorrect) {
            classes.push("answer-option--correct");
          }
          if (answerOption.selected) {
            classes.push("selected-answer-option");
            classes.push(
              answerOption.isCorrect
                ? "selected-answer-option--correct"
                : "selected-answer-option--incorrect",
            );
          }
        } else if (selectedAnswerId === answerOption.id) {
          classes.push("selected-answer-option");
        }

          const isEliminated = eliminatedIds.has(answerOption.id);

          return (
          <div
            className={classes.join(" ")}
            onClick={(e) => handleCardClick(e, answerOption.id)}
            id={answerOption.id.toString()}
              style={
                isEliminated
                  ? {
                      opacity: 0.65,
                      filter: "grayscale(0.75)",
                      backgroundColor: "#fff5f5",
                      border: "1px dashed #f0a8a8",
                      boxShadow: "none",
                      outline: "none",
                      transition: "none",
                    }
                  : undefined
              }
          >
            <div>
                <div className="top-part" style={{ display: "flex", alignItems: "center" }}>
                  <label htmlFor={option}>{option}</label>
                  <button
                    type="button"
                    disabled={practiceQuestionAnswered}
                    aria-label={isEliminated ? "Uncross option" : "Cross out option"}
                    title={isEliminated ? "Uncross option" : "Cross out option"}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleEliminated(answerOption.id);
                    }}
                    style={{
                      marginLeft: 8,
                      border: "none",
                      background: "transparent",
                      padding: 2,
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: practiceQuestionAnswered ? "not-allowed" : "pointer",
                      opacity: practiceQuestionAnswered ? 0.6 : 1,
                    }}
                  >
                    {isEliminated ? (
                      <BsArrowCounterclockwise size={16} color="#cc0000" />
                    ) : (
                      <BsXCircle size={16} color="#666" />
                    )}
                  </button>

              <div style={{ marginLeft: "10px" }}>
                <span
                  style={
                    isEliminated
                      ? { textDecoration: "line-through", color: "#a33" }
                      : undefined
                  }
                >
                  <Latex delimiters={delimeters}>{answerOption.text}</Latex>
                </span>
              </div>

                  {/* correctness icons will render in the right-aligned area after submission */}
                  <span
                    style={{
                      marginLeft: "auto",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 28,
                    }}
                  >
                    {practiceQuestionAnswered ? (
                      answerOption.isCorrect ? (
                        <IoIosCheckmarkCircle color="green" size={24} />
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 20,
                            height: 20,
                            backgroundColor: "red",
                            borderRadius: "50%",
                          }}
                        >
                          <ImCross color="white" size={10} />
                        </span>
                      )
                    ) : (
                      <input
                        type="checkbox"
                        disabled={practiceQuestionAnswered}
                        name={option}
                        id={option}
                        checked={selectedAnswerId === answerOption.id}
                        onChange={() => {}}
                      />
                    )}
                  </span>
              </div>
              {/* <hr /> */}
              {/* <div className="bottom-part">
                <p
                  style={
                    isEliminated
                      ? { textDecoration: "line-through", color: "#a33" }
                      : undefined
                  }
                >
                  <Latex delimiters={delimeters}>{answerOption.text}</Latex>
                </p>
                {answerOption.imageUrl && (
                  <div style={isEliminated ? { filter: "grayscale(1)", opacity: 0.7 } : undefined}>
                    <ZoomImage src={answerOption.imageUrl} width={200}></ZoomImage>
                  </div>
                )}
              </div> */}
            </div>
          </div>
        );
      })}

      </div>
      {!practiceQuestionAnswered ? (
        <div style={{ width: "100%", marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 10 }}>
          {(selectedAnswerId !== undefined || eliminatedIds.size > 0) ? (
            <Button
              variant="danger"
              onClick={() => {
                setEliminatedIds(new Set());
                onChangeSelectedAnswer(undefined);
              }}
            >
              Clear
            </Button>
          ) : null}
          <Button onClick={onSubmit}>Submit answer</Button>
        </div>
      ) : null}
    </>
  );
};


